import React, { useRef, useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import withRouter from "../../../components/Common/withRouter";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  FormFeedback,
  Label,
  Input,
} from "reactstrap";
import MediaModel from "../MediaUpload/MediaModel";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
const Setting = () => {
  const baseurl = process.env.REACT_APP_BASE_URL;
  const imgurl = process.env.REACT_APP_IMAGE_URL;

  const [settingData, setSettingData] = useState(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [imageModel, setImageModel] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(false);

  // ✅ Fetch setting data on mount
  const fetchSettingData = async () => {
    try {
      const response = await fetch(`${baseurl}/admin/master/setting/1`);
      if (!response.ok) throw new Error("Failed to fetch settings");
      const data = await response.json();
      setSettingData(data);

      // Pre-fill Formik form
      formik.setValues({
        logo: data.logo || "",
        address: data.address || "",
        email: data.email || "",
        customerCareNumber: data.customer_care_number || "",
        facebook: data.facebook || "",
        twitter: data.twitter || "",
        linkedin: data.linkedin || "",
        youtube: data.youtube || "",
        instagram: data.instagram || "",
        totalProducts: data.total_product || "",
        newProducts: data.new_product || "",
        individualDuration: data.lcd_individual || "",
        corporateDuration: data.lcd_corporate || "",
        cartExpire: data.cart_expire || "",
      });

      // Set uploaded image preview if exists
      if (data.logo) setUploadedImages([imgurl + data.logo]);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  useEffect(() => {
    fetchSettingData();
    // eslint-disable-next-line
  }, []);

  // ✅ Formik setup
  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      logo: settingData?.logo || "",
      address: settingData?.address || "",
      email: settingData?.email || "",
      customerCareNumber: settingData?.customer_care_number || "",
      facebook: settingData?.facebook || "",
      twitter: settingData?.twitter || "",
      linkedin: settingData?.linkedin || "",
      youtube: settingData?.youtube || "",
      instagram: settingData?.instagram || "",
      totalProducts: settingData?.total_product || "",
      newProducts: settingData?.new_product || "",
      individualDuration: settingData?.lcd_individual || "",
      corporateDuration: settingData?.lcd_corporate || "",
      cartExpire: settingData?.cart_expire || "",
    },
    validationSchema: Yup.object({
      logo: Yup.mixed().test(
              "fileSelected",
              "Please select an image",
              () => uploadedImages && uploadedImages.length > 0
            ),
      address: Yup.string().required("Address is required"),
      email: Yup.string().email("Invalid email format").required("Email is required"),
      customerCareNumber: Yup.string()
        .matches(/^\d{10}$/, "Must be a valid 10-digit number")
        .required("Customer Care Number is required"),
      facebook: Yup.string().url("Invalid URL").nullable(),
      twitter: Yup.string().url("Invalid URL").nullable(),
      linkedin: Yup.string().url("Invalid URL").nullable(),
      youtube: Yup.string().url("Invalid URL").nullable(),
      instagram: Yup.string().url("Invalid URL").nullable(),
      totalProducts: Yup.number().positive("Must be positive").required("Required"),
      newProducts: Yup.number().min(0, "Cannot be negative").required("Required"),
      individualDuration: Yup.number().positive("Must be positive").required("Required"),
      corporateDuration: Yup.number().positive("Must be positive").required("Required"),
      cartExpire: Yup.number().positive("Must be positive").required("Required"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const payload = {
          logo: values.logo || "",
          address: values.address,
          email: values.email,
          customer_care_number: values.customerCareNumber,
          facebook: values.facebook,
          twitter: values.twitter,
          linkedin: values.linkedin,
          youtube: values.youtube,
          instagram: values.instagram,
          lcd_individual: Number(values.individualDuration),
          lcd_corporate: Number(values.corporateDuration),
          total_product: Number(values.totalProducts),
          new_product: Number(values.newProducts),
          cart_expire: Number(values.cartExpire),
        };

        const response = await fetch(`${baseurl}/admin/master/setting/1`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (response.ok) {
          toast.success('Settings updated successfully!', 'success');
          
          fetchSettingData();
        } else {
          toast.error('Failed to update settings', 'error');
        }
      } catch (err) {
        toast.error('An error occurred while updating settings', 'error');
      } finally {
        setLoading(false);
      }
    },
  });

  // ✅ Image modal handlers
  const toggleImageModal = () => {
    setImageModel(!imageModel);
    setSelectedImage([]);
  };

  const handleUploadImage = (image) => {
    if (image) {
      setUploadedImages([imgurl + image[0].fileurl]);
      formik.setFieldValue("logo", image[0].fileurl);
    }
    toggleImageModal();
    setSelectedImage(null);
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const newImage = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      setUploadedImages([imgurl + newImage]);
    }
  };

  // ✅ Helper renderers
  const renderSocialMediaLinks = () => (
    <Row>
      {["facebook", "twitter", "linkedin", "youtube", "instagram"].map((field) => (
        <Col lg={6} key={field}>
          <div className="mb-3">
            <Label className="text-capitalize">{field}</Label>
            <Input
              type="text"
              name={field}
              placeholder={`Enter ${field} link`}
              {...formik.getFieldProps(field)}
              invalid={formik.touched[field] && !!formik.errors[field]}
            />
            {formik.touched[field] && formik.errors[field] && (
              <FormFeedback>{formik.errors[field]}</FormFeedback>
            )}
          </div>
        </Col>
      ))}
    </Row>
  );

  const renderNumberUpdates = () => (
    <Row>
      {["totalProducts", "newProducts", "cartExpire"].map((field) => (
        <Col lg={6} key={field}>
          <div className="mb-3">
            <Label>
              {field === "totalProducts"
                ? "Total Products"
                : field === "newProducts"
                ? "New Added Products"
                : "Cart Expiration (in Days)"}
            </Label>
            <Input
              type="number"
              name={field}
              placeholder={`Enter ${field}`}
              {...formik.getFieldProps(field)}
              invalid={formik.touched[field] && !!formik.errors[field]}
            />
            {formik.touched[field] && formik.errors[field] && (
              <FormFeedback>{formik.errors[field]}</FormFeedback>
            )}
          </div>
        </Col>
      ))}
    </Row>
  );

  document.title = "Admin Settings | Vekart";

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col className="mx-auto">
            <Card>
              <CardBody>
                <h4 className="mb-4">Admin Settings</h4>
                <form onSubmit={formik.handleSubmit}>
                  <Row>
                    {/* Logo Upload */}
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>Logo Upload (via Media Gallery)</Label>
                        <Input
                                                name="image"
                                                type="file"
                                                accept="image/jpeg, image/png"
                                                onChange={handleImageChange}
                                                
                                                style={{ display: "none" }}
                                                invalid={
                                                  formik.touched.image && formik.errors.image
                                                    ? true
                                                    : false
                                                }
                                              />
                                              <div
                                                className="custom-file-button"
                                                onClick={toggleImageModal}
                                              >
                                                <i
                                                  className="bx bx-cloud-upload me-2"
                                                  style={{ fontSize: "25px" }}
                                                ></i>
                                                Choose File
                                              </div>
                                              {formik.errors.image && formik.touched.image ? (
                                                <FormFeedback type="invalid" className="d-block">
                                                  {formik.errors.image}
                                                </FormFeedback>
                                              ) : null}
                        
                        {formik.errors.logo && formik.touched.logo && (
                          <FormFeedback type="invalid" className="d-block">
                            {formik.errors.logo}
                          </FormFeedback>
                        )}

                        {uploadedImages && (
                                            <Card className="mt-1 mb-3 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                                              <div className="p-2">
                                                <Row className="d-flex justify-content-between align-items-center">
                                                  <Col className="col-auto">
                                                    <img
                                                      data-dz-thumbnail=""
                                                      height="100"
                                                      width="100"
                                                      className="avatar-md rounded bg-light"
                                                      alt="images"
                                                      src={uploadedImages}
                                                    />
                                                  </Col>
                                                  <Col className="col-auto">
                                                    <button
                                                      type="button"
                                                      className="btn btn-danger btn-sm"
                                                      onClick={() => {
                                                        setUploadedImages(null);
                                                      }}
                                                    >
                                                      Delete
                                                    </button>
                                                  </Col>
                                                </Row>
                                              </div>
                                            </Card>
                                          )}
                      </div>
                    </Col>

                    {/* Address */}
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>Address</Label>
                        <Input
                          type="text"
                          name="address"
                          placeholder="Enter Address"
                          {...formik.getFieldProps("address")}
                          invalid={formik.touched.address && !!formik.errors.address}
                        />
                        {formik.touched.address && formik.errors.address && (
                          <FormFeedback>{formik.errors.address}</FormFeedback>
                        )}
                      </div>
                    </Col>

                    {/* Email */}
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>Email</Label>
                        <Input
                          type="email"
                          name="email"
                          placeholder="Enter Email"
                          {...formik.getFieldProps("email")}
                          invalid={formik.touched.email && !!formik.errors.email}
                        />
                        {formik.touched.email && formik.errors.email && (
                          <FormFeedback>{formik.errors.email}</FormFeedback>
                        )}
                      </div>
                    </Col>

                    {/* Customer Care Number */}
                    <Col md={6}>
                      <div className="mb-3">
                        <Label>Customer Care Number</Label>
                        <Input
                          type="text"
                          name="customerCareNumber"
                          placeholder="Enter 10-digit number"
                          {...formik.getFieldProps("customerCareNumber")}
                          invalid={
                            formik.touched.customerCareNumber &&
                            !!formik.errors.customerCareNumber
                          }
                        />
                        {formik.touched.customerCareNumber &&
                          formik.errors.customerCareNumber && (
                            <FormFeedback>{formik.errors.customerCareNumber}</FormFeedback>
                          )}
                      </div>
                    </Col>
                  </Row>

                  <h5 className="mt-4">Social Media Links</h5>
                  {renderSocialMediaLinks()}

                  <h5 className="mt-4">Login Activation Duration (in Months)</h5>
                  <Row>
                    <Col lg={6}>
                      <div className="mb-3">
                        <Label>Individual Customer</Label>
                        <Input
                          type="number"
                          name="individualDuration"
                          placeholder="Enter months"
                          {...formik.getFieldProps("individualDuration")}
                          invalid={
                            formik.touched.individualDuration &&
                            !!formik.errors.individualDuration
                          }
                        />
                        {formik.touched.individualDuration &&
                          formik.errors.individualDuration && (
                            <FormFeedback>{formik.errors.individualDuration}</FormFeedback>
                          )}
                      </div>
                    </Col>
                    <Col lg={6}>
                      <div className="mb-3">
                        <Label>Corporate Customer</Label>
                        <Input
                          type="number"
                          name="corporateDuration"
                          placeholder="Enter months"
                          {...formik.getFieldProps("corporateDuration")}
                          invalid={
                            formik.touched.corporateDuration &&
                            !!formik.errors.corporateDuration
                          }
                        />
                        {formik.touched.corporateDuration &&
                          formik.errors.corporateDuration && (
                            <FormFeedback>{formik.errors.corporateDuration}</FormFeedback>
                          )}
                      </div>
                    </Col>
                  </Row>

                  <h5 className="mt-4">Number Updates</h5>
                  {renderNumberUpdates()}

                  <div className="text-center mt-4">
                    <Button type="submit" color="success" disabled={loading}>
                      {loading ? "Saving..." : "Save Settings"}
                    </Button>
                  </div>
                </form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ✅ Media Gallery Modal */}
        <MediaModel
          imageModel={imageModel}
          toggleModal={toggleImageModal}
          handleUploadImage={handleUploadImage}
          selectedImage={selectedImage}
          setSelectedImage={setSelectedImage}
        />
      </Container>
    </div>
  );
};

export default withRouter(Setting);
